"use client";

import { AnimatePresence, motion } from "framer-motion";
import React, { KeyboardEvent, MouseEvent, ReactNode, useState } from "react";

import Icon from "@/components/Icons/Icon";
import IconPlus from "@/components/Icons/IconPlus";

interface CollapsibleSectionProps {
  title: string;
  children: ReactNode;
  isOpen?: boolean;
  className?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, isOpen = false, className = "" }) => {
  const [expanded, setExpanded] = useState<boolean>(isOpen);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setExpanded(!expanded);
    }
  };

  const toggleExpanded = (e: MouseEvent<HTMLDivElement>) => {
    setExpanded((prev) => !prev);
  };

  return (
    <div
      className={`rounded-2xl bg-white p-4 ${className} relative cursor-pointer`}
      onClick={toggleExpanded}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={handleKeyDown}
    >
      <div
        className={`flex items-center justify-between ${expanded ? "mb-4 border-b-2 border-slate-50 pb-3" : "pb-1"}`}
      >
        <h3 className="font-urbanist text-base font-bold text-primary-foreground">{title}</h3>
        <div>
          <Icon
            height={10}
            width={10}
            className="bg-black"
            backgroundColor="secondary-light"
            icon={
              <IconPlus
                className={`text-gray-500 transition-transform duration-300 ease-in-out ${
                  expanded ? "rotate-45" : "rotate-0"
                }`}
              />
            }
          />
        </div>
      </div>

      <AnimatePresence initial={false} mode="wait">
        {expanded && (
          <motion.div
            key="content"
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: 1,
              height: "auto",
              transition: {
                opacity: { duration: 0.3, ease: "easeOut", delay: 0.1 },
                height: { duration: 0.3, ease: "easeOut" },
              },
            }}
            exit={{
              opacity: 0,
              height: 0,
              transition: {
                opacity: { duration: 0.1, ease: "easeOut" },
                height: { duration: 0.15, ease: "easeInOut" },
              },
            }}
            style={{ pointerEvents: "auto", position: "relative" }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              role="region"
              tabIndex={-1}
              className="py-2"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: 0.3, ease: "easeOut", delay: 0.15 },
              }}
              exit={{
                opacity: 0,
                transition: { duration: 0.1, ease: "easeOut" },
              }}
            >
              {children}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollapsibleSection;
